import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  createAccountPhoneSchema,
  createAccountOtpSchema,
  type CreateAccountPhoneValues,
} from "@src/packages/schemas/createAccount";
import {
  View,
  Text,
  TextInput,
  Pressable,
  Animated,
  Platform,
} from "react-native";
import * as Localization from "expo-localization";
import type { CountryCode } from "libphonenumber-js";
import type {
  NativeStackScreenProps,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { useNavigation } from "@react-navigation/native";
import { ArrowRight, KeyRound, Phone, RotateCcw } from "lucide-react-native";
import { OTPInput } from "@src/packages/form/OTPInput/OTPInput";
import OnboardingShell from "../components/OnboardingShell";
import {
  LouiseMark,
  MovingGradientTitle,
  OnboardingPrimaryButton,
} from "../components/OnboardingLayout";
import {
  AppleIcon,
  AuthDivider,
  GlassButton,
  GlassAuthButton,
  GoogleIcon,
  MoonPhoneField,
  TextActionButton,
  FieldMessage,
} from "@louise/moon-ui";
import {
  findMoonPhoneCountry,
  formatMoonPhoneNumber,
  MOON_PHONE_COUNTRIES,
  type MoonPhoneCountry,
} from "@src/packages/moon-ui/phoneCountries";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { EMBER } from "../theme";
import type { OnboardingStackParamList } from "../types";
import type { RootStackParamList } from "../../App";
import { useStytch, useStytchUser } from "../../utils/useStytch";
import { useOAuth } from "../../utils/useOAuth";
import { useTestSignIn } from "../../utils/useTestSignIn";
import {
  useSaveOnboardingToApi,
  PartnerAcceptFailedError,
  type PartnerInviteFailureReason,
  type PartnerInviteFailureStage,
} from "../../main/useSaveOnboardingData";
import {
  createOnboardingSaveKey,
  createOnboardingSaveIdempotencyKey,
  runOnboardingSaveOnce,
} from "../../main/onboardingSaveGuard";
import { useOnboardingAnalytics } from "../analytics";
import { useFlowAnalytics } from "@src/packages/analytics/useFlowAnalytics";
import { INVITE_ACCEPT_FLOW } from "@src/packages/analytics/flows";
import { useReferral } from "../../referral/ReferralContext";
import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import { getIdsFromMetadata } from "@src/packages/api/louise-api";
import { getIpCountryCode } from "@src/packages/location/ipCountry";
import { toE164PhoneNumber } from "../phoneE164";
import {
  isPasskeySignInSupported,
  useAuthenticatePasskey,
} from "@src/packages/auth/hooks/use2FA";
import {
  normalizePhoneCountryCode,
  resolvePhoneCountryPreference,
} from "@src/packages/location/phoneCountryPreference";
import { semanticColors } from "@louise/theme/tokens";

type Props = NativeStackScreenProps<OnboardingStackParamList, "CreateAccount">;
export type AccountAccessVariant = "signup" | "login";

type AccountAccessProps = {
  navigation: Pick<
    NativeStackNavigationProp<OnboardingStackParamList, "CreateAccount">,
    "navigate"
  >;
  variant: AccountAccessVariant;
  initialError?: string;
};

/* ---------- Phone country selection ---------- */

const DEFAULT_PHONE_COUNTRY = findMoonPhoneCountry("US")!;
const SUPPORTED_PHONE_COUNTRY_CODES = MOON_PHONE_COUNTRIES.map(
  ({ iso }) => iso,
);
const MAX_PHONE_DIGITS = 15;
const RESEND_COOLDOWN_SECONDS = 30;
const SETUP_METADATA_TIMEOUT_MS = 10_000;
const SETUP_METADATA_RETRY_DELAY_MS = 500;
const SETUP_METADATA_REFRESH_TIMEOUT_MS = 2_500;

const MESSAGE_KEYS = [
  {
    key: "profile",
    textKey: "onboarding.create_account.loading_putting_together",
  },
  { key: "match", textKey: "onboarding.create_account.loading_mapping" },
  { key: "plan", textKey: "onboarding.create_account.loading_personalizing" },
  { key: "ready", textKey: "onboarding.create_account.loading_ready" },
];

function isOtpDevFixtureEnabled() {
  if (Platform.OS !== "web" || typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("test") === "1" && params.get("dev_account_step") === "otp";
}

/* ---------- Main screen ---------- */

export default function CreateAccountScreen({ navigation }: Props) {
  return <AccountAccessScreen navigation={navigation} variant="signup" />;
}

export function AccountAccessScreen({
  navigation,
  variant,
  initialError,
}: AccountAccessProps) {
  const t = useTranslations();
  const capturePress = usePressCapture("AccountAccessScreen");
  const rootNav =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const stytch = useStytch();
  const { user } = useStytchUser();
  const {
    startOAuth,
    isLoading: isOAuthLoading,
    error: oauthError,
  } = useOAuth();
  const authenticatePasskey = useAuthenticatePasskey();
  const {
    data,
    save: saveToApi,
    retryPartnerInvite,
    clearOnboarding,
  } = useSaveOnboardingToApi();
  const analytics = useOnboardingAnalytics();
  // Signup and invite acceptance share this screen but are different funnels:
  // these fire only in partner mode, where accepting the invite is the thing
  // that either lands or is lost.
  const inviteFlow = useFlowAnalytics(INVITE_ACCEPT_FLOW);
  const { captureSignupCompleted } = useReferral();
  const saveToApiRef = useRef(saveToApi);
  const analyticsRef = useRef(analytics);
  const inviteFlowRef = useRef(inviteFlow);
  const captureSignupCompletedRef = useRef(captureSignupCompleted);
  const isMountedRef = useRef(true);
  saveToApiRef.current = saveToApi;
  analyticsRef.current = analytics;
  inviteFlowRef.current = inviteFlow;
  captureSignupCompletedRef.current = captureSignupCompleted;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const isOtpDevFixture = isOtpDevFixtureEnabled();
  const startsInWelcome = variant === "login" || isOtpDevFixture;
  const passkeyAvailable = variant === "login" && isPasskeySignInSupported();

  const [phase, setPhase] = useState<
    "loading" | "welcome" | "saving" | "setup-recovery" | "partner-recovery"
  >(startsInWelcome ? "welcome" : "loading");
  const [step, setStep] = useState<"phone" | "otp">(
    isOtpDevFixture ? "otp" : "phone",
  );
  const [visibleCount, setVisibleCount] = useState(
    startsInWelcome ? MESSAGE_KEYS.length : 0,
  );
  const [phoneRaw, setPhoneRaw] = useState(isOtpDevFixture ? "5551234567" : "");
  const [phoneValue, setPhoneValue] = useState(
    isOtpDevFixture ? `${DEFAULT_PHONE_COUNTRY.dialCode}5551234567` : "",
  );
  const [methodId, setMethodId] = useState(
    isOtpDevFixture ? "dev-otp-method" : "",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(initialError ?? null);
  const [resendCooldown, setResendCooldown] = useState(
    isOtpDevFixture ? RESEND_COOLDOWN_SECONDS : 0,
  );
  const [otpResetKey, setOtpResetKey] = useState(0);
  const [isSavingOnboarding, setIsSavingOnboarding] = useState(false);
  const [isAnimationDone, setIsAnimationDone] = useState(startsInWelcome);
  const [isSaveDone, setIsSaveDone] = useState(false);
  const [partnerInviteFailure, setPartnerInviteFailure] =
    useState<PartnerInviteFailureReason | null>(null);
  const [partnerInviteFailureStage, setPartnerInviteFailureStage] =
    useState<PartnerInviteFailureStage>("accept");
  const [isRetryingPartnerInvite, setIsRetryingPartnerInvite] = useState(false);
  const savedPhoneCountry = useMemo(() => {
    return findMoonPhoneCountry(normalizePhoneCountryCode(data.country));
  }, [data.country]);
  const localeCountry = useMemo(
    () => Localization.getLocales()[0]?.regionCode ?? null,
    [],
  );
  const initialPhoneCountryCode = useMemo(
    () =>
      resolvePhoneCountryPreference({
        savedCountry: data.country,
        localeCountry,
        supportedCountryCodes: SUPPORTED_PHONE_COUNTRY_CODES,
        fallbackCountry: DEFAULT_PHONE_COUNTRY.iso,
      }).countryCode,
    [data.country, localeCountry],
  );
  const initialPhoneCountry =
    findMoonPhoneCountry(initialPhoneCountryCode) ?? DEFAULT_PHONE_COUNTRY;
  const [country, setCountry] = useState<MoonPhoneCountry>(
    isOtpDevFixture ? DEFAULT_PHONE_COUNTRY : initialPhoneCountry,
  );
  const hasCountryInteraction = useRef(isOtpDevFixture);
  const hasPhoneInteraction = useRef(isOtpDevFixture);

  useEffect(() => {
    if (!oauthError) return;
    setError(t("onboarding.login.oauth_error"));
  }, [oauthError, t]);

  useEffect(() => {
    if (!savedPhoneCountry || hasCountryInteraction.current) return;
    setCountry(savedPhoneCountry);
  }, [savedPhoneCountry]);

  useEffect(() => {
    if (Platform.OS !== "web" || isOtpDevFixture || savedPhoneCountry) return;

    const controller = new AbortController();
    void getIpCountryCode(controller.signal).then((ipCountry) => {
      if (
        !ipCountry ||
        hasCountryInteraction.current ||
        hasPhoneInteraction.current
      ) {
        return;
      }

      const preference = resolvePhoneCountryPreference({
        savedCountry: data.country,
        ipCountry,
        localeCountry,
        supportedCountryCodes: SUPPORTED_PHONE_COUNTRY_CODES,
        fallbackCountry: DEFAULT_PHONE_COUNTRY.iso,
      });
      const nextCountry = findMoonPhoneCountry(preference.countryCode);
      if (preference.source === "ip" && nextCountry) {
        setCountry(nextCountry);
      }
    });

    return () => controller.abort();
  }, [data.country, isOtpDevFixture, localeCountry, savedPhoneCountry]);

  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setTimeout(() => {
      setResendCooldown((current) => Math.max(0, current - 1));
    }, 1000);

    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // RHF/Zod owns validity of the phone field (#1545). MoonPhoneField owns
  // formatting and country detection; this screen mirrors its canonical value
  // into the raw digits used by the onboarding save and OTP paths.
  const phoneForm = useForm<CreateAccountPhoneValues>({
    resolver: zodResolver(createAccountPhoneSchema),
    mode: "onChange",
    defaultValues: { phoneDigits: "" },
  });

  useEffect(() => {
    phoneForm.setValue("phoneDigits", phoneRaw.replace(/\D/g, ""), {
      shouldValidate: phoneForm.formState.isSubmitted,
    });
  }, [phoneRaw, phoneForm]);

  const handlePhoneFieldChange = useCallback((next: string) => {
    hasPhoneInteraction.current = true;
    setPhoneValue(next);
  }, []);

  const handlePhoneNationalChange = useCallback(
    (digits: string, nextCountry: CountryCode) => {
      const detected = findMoonPhoneCountry(nextCountry);
      if (detected) setCountry(detected);
      setPhoneRaw(digits.replace(/\D/g, "").slice(0, MAX_PHONE_DIGITS));
    },
    [],
  );

  const handlePhoneCountryChange = useCallback((next: CountryCode) => {
    hasCountryInteraction.current = true;
    const nextCountry = findMoonPhoneCountry(next);
    if (nextCountry) setCountry(nextCountry);
  }, []);
  // Loading phase
  const msgOpacities = useRef(
    MESSAGE_KEYS.map(() => new Animated.Value(startsInWelcome ? 1 : 0)),
  ).current;
  const orbScale = useRef(
    new Animated.Value(startsInWelcome ? 1 : 0.8),
  ).current;
  const orbOpacity = useRef(
    new Animated.Value(startsInWelcome ? 1 : 0),
  ).current;
  const loadingOpacity = useRef(
    new Animated.Value(startsInWelcome ? 0 : 1),
  ).current;

  // Welcome phase
  const welcomeOpacity = useRef(
    new Animated.Value(startsInWelcome ? 1 : 0),
  ).current;
  const authOpacity = useRef(
    new Animated.Value(startsInWelcome ? 1 : 0),
  ).current;
  const authTranslateY = useRef(
    new Animated.Value(startsInWelcome ? 0 : 24),
  ).current;

  // The reveal cadence below is content pacing, not decoration, so reduced
  // motion collapses each animation to its end state and leaves the timing
  // that drives the loading narrative intact.
  const reducedMotion = useReducedMotion();
  const reducedMotionRef = useRef(reducedMotion);
  reducedMotionRef.current = reducedMotion;

  // The orb is the only reveal that starts on mount, before native has
  // resolved the preference. Restarting it at zero duration lands it on its
  // end state instead of leaving it mid-scale.
  useEffect(() => {
    if (startsInWelcome) return;

    const entrance = Animated.parallel([
      Animated.timing(orbOpacity, {
        toValue: 1,
        duration: reducedMotion ? 0 : 600,
        useNativeDriver: true,
      }),
      Animated.timing(orbScale, {
        toValue: 1,
        duration: reducedMotion ? 0 : 800,
        useNativeDriver: true,
      }),
    ]);
    entrance.start();
    return () => entrance.stop();
  }, [startsInWelcome, reducedMotion, orbOpacity, orbScale]);

  useEffect(() => {
    if (startsInWelcome) return;

    const motionDuration = (ms: number) => (reducedMotionRef.current ? 0 : ms);

    // Sequential message reveals
    const timers: ReturnType<typeof setTimeout>[] = [];
    MESSAGE_KEYS.forEach((_, i) => {
      timers.push(
        setTimeout(
          () => {
            setVisibleCount(i + 1);
            const opacity = msgOpacities[i];
            if (opacity) {
              Animated.timing(opacity, {
                toValue: 1,
                duration: motionDuration(400),
                useNativeDriver: true,
              }).start();
            }
          },
          800 + i * 900,
        ),
      );
    });

    // After messages finish, either redirect (if authenticated) or show auth form
    const transitionDelay = 800 + MESSAGE_KEYS.length * 900 + 600;
    timers.push(
      setTimeout(() => {
        Animated.timing(loadingOpacity, {
          toValue: 0,
          duration: motionDuration(500),
          useNativeDriver: true,
        }).start(() => {
          setIsAnimationDone(true);

          // If user is already authenticated, start the save/redirect phase
          if (user) {
            setPhase("saving");
            return;
          }

          setPhase("welcome");

          // Fade in welcome text
          Animated.timing(welcomeOpacity, {
            toValue: 1,
            duration: motionDuration(800),
            useNativeDriver: true,
          }).start();

          // Slide up auth card after brief delay
          setTimeout(() => {
            Animated.parallel([
              Animated.timing(authOpacity, {
                toValue: 1,
                duration: motionDuration(600),
                useNativeDriver: true,
              }),
              Animated.timing(authTranslateY, {
                toValue: 0,
                duration: motionDuration(600),
                useNativeDriver: true,
              }),
            ]).start();
          }, 300);
        });
      }, transitionDelay),
    );

    return () => {
      timers.forEach(clearTimeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // In partner-invite mode, the data is still in onboarding context when we
  // finish saving (PartnerJourneyConfirm clears it). Capture the mode now so
  // we route there instead of jumping straight to Home.
  const isPartnerMode = !!data.partnerInvite;
  // Read from a ref inside the save effect: by the time it resolves,
  // PartnerJourneyConfirm may already have cleared the invite from context.
  const isPartnerModeRef = useRef(isPartnerMode);
  isPartnerModeRef.current = isPartnerMode;

  const goHome = useCallback(() => {
    if (variant === "signup" && isPartnerMode) {
      navigation.navigate("PartnerJourneyConfirm");
      return;
    }
    rootNav.reset({ index: 0, routes: [{ name: "Home" }] });
  }, [isPartnerMode, navigation, rootNav, variant]);

  const goHomeUnconnected = useCallback(() => {
    if (partnerInviteFailureStage === "profile") return;
    clearOnboarding();
    analytics.abandoned("partner-accept-skipped", data);
    inviteFlow.abandoned("skipped_after_failure", { step: "accept" });
    captureSignupCompleted();
    rootNav.reset({ index: 0, routes: [{ name: "Home" }] });
  }, [
    analytics,
    inviteFlow,
    captureSignupCompleted,
    clearOnboarding,
    data,
    partnerInviteFailureStage,
    rootNav,
  ]);

  // If auth completes while we're on the welcome state (e.g. OAuth return),
  // transition into saving and persist onboarding before redirecting.
  useEffect(() => {
    if (phase === "welcome" && user && !error) {
      if (variant === "login") {
        goHome();
      } else {
        setPhase("saving");
      }
    }
  }, [error, goHome, phase, user, variant]);

  // Redirect only after both locks complete.
  useEffect(() => {
    if (variant === "signup" && isAnimationDone && isSaveDone) {
      goHome();
    }
  }, [goHome, isAnimationDone, isSaveDone, variant]);

  useEffect(() => {
    if (
      variant === "login" ||
      phase === "welcome" ||
      phase === "setup-recovery" ||
      phase === "partner-recovery" ||
      !user ||
      isSavingOnboarding ||
      isSaveDone
    ) {
      return;
    }
    setError(null);
    setIsSavingOnboarding(true);

    let saveStage: "metadata" | "patient" = "metadata";

    // Wait for backend to provision trusted_metadata (tenantId + patientId)
    // before saving onboarding data, similar to Olly's retry loop.
    const waitForMetadataAndSave = async () => {
      const MAX_RETRIES = 15;
      const metadataDeadline = Date.now() + SETUP_METADATA_TIMEOUT_MS;
      let hasMetadata = false;
      for (let i = 0; i < MAX_RETRIES; i++) {
        if (!isMountedRef.current) return;
        if (Date.now() >= metadataDeadline) break;
        const metadata = stytch.user.getSync()?.trusted_metadata ?? {};
        const { tenantId, patientId } = getIdsFromMetadata(
          metadata as Record<string, unknown>,
        );
        if (tenantId && patientId) {
          hasMetadata = true;
          break;
        }

        // Refresh session to get updated metadata from backend
        try {
          const remaining = metadataDeadline - Date.now();
          await Promise.race([
            stytch.session.authenticate({
              session_duration_minutes: 60 * 24,
            }),
            new Promise<never>((_, reject) => {
              setTimeout(
                () => reject(new Error("Metadata refresh timed out")),
                Math.min(SETUP_METADATA_REFRESH_TIMEOUT_MS, remaining),
              );
            }),
          ]);
        } catch {
          // ignore refresh errors, keep retrying
        }

        const remaining = metadataDeadline - Date.now();
        if (i < MAX_RETRIES - 1 && remaining > 0) {
          await new Promise((r) =>
            setTimeout(r, Math.min(SETUP_METADATA_RETRY_DELAY_MS, remaining)),
          );
        }
      }

      if (!isMountedRef.current) return;

      if (!hasMetadata) {
        // Surface the failure so the user can retry instead of silently
        // proceeding to home with their onboarding data still local.
        throw new Error(
          "Backend did not provision tenant_slug / patient_id within the retry window. Onboarding data was not saved.",
        );
      }

      const stytchPhone = user.phone_numbers?.[0]?.phone_number;
      saveStage = "patient";
      // Single-flight: OnboardingSaveBoot may have started the same save
      // (native OAuth / any authed path that skips this screen, #1393).
      const saveKey = createOnboardingSaveKey(user.user_id, data, stytchPhone);
      const idempotencyKey = createOnboardingSaveIdempotencyKey(saveKey);
      await runOnboardingSaveOnce(saveKey, () =>
        saveToApiRef.current(data, stytchPhone, idempotencyKey),
      );
    };

    waitForMetadataAndSave()
      .then(() => {
        if (!isMountedRef.current) return;
        setIsSaveDone(true);
        analyticsRef.current.completed(data);
        if (isPartnerModeRef.current) inviteFlowRef.current.completed();
        captureSignupCompletedRef.current();
      })
      .catch((err) => {
        if (!isMountedRef.current) return;
        console.error("Failed to save onboarding data:", err);
        if (err instanceof PartnerAcceptFailedError) {
          setPartnerInviteFailure(err.reason);
          setPartnerInviteFailureStage(err.stage);
          setIsSavingOnboarding(false);
          setIsSaveDone(false);
          setPhase("partner-recovery");
          analyticsRef.current.abandoned("partner-accept-failed", data);
          inviteFlowRef.current.abandoned("accept_failed", {
            stage: err.stage,
          });
          return;
        }
        setError(t("onboarding.create_account.error_setup_failed"));
        setIsSavingOnboarding(false);
        setIsSaveDone(false);
        setPhase("setup-recovery");
        analyticsRef.current.abandoned(
          saveStage === "patient"
            ? "patient-save-failed"
            : "metadata-retry-failed",
          data,
        );
      });
  }, [phase, user, isSavingOnboarding, isSaveDone, data, stytch, t, variant]);

  const handleRetryPartnerInvite = useCallback(async () => {
    setIsRetryingPartnerInvite(true);
    setPartnerInviteFailure(null);
    setPartnerInviteFailureStage("accept");
    try {
      await retryPartnerInvite(user?.phone_numbers?.[0]?.phone_number);
      setIsSaveDone(true);
      analytics.completed(data);
      // This path only exists in partner mode — it is the retry offered after
      // the accept failed — so no mode guard is needed here.
      inviteFlow.completed();
      captureSignupCompleted();
    } catch (err) {
      if (err instanceof PartnerAcceptFailedError) {
        setPartnerInviteFailure(err.reason);
        setPartnerInviteFailureStage(err.stage);
      } else {
        setPartnerInviteFailure("unavailable");
      }
    } finally {
      setIsRetryingPartnerInvite(false);
    }
  }, [
    analytics,
    inviteFlow,
    captureSignupCompleted,
    data,
    retryPartnerInvite,
    user,
  ]);

  // Both Stytch SDKs (@stytch/react on web, @stytch/react-native on native)
  // throw errors carrying a structured `error_type` slug
  // (stytch.com/docs/api/errors) — classify on that, never on message prose.
  const stytchErrorType = (err: unknown): string | null =>
    err instanceof Error &&
    "error_type" in err &&
    typeof err.error_type === "string"
      ? err.error_type
      : null;

  const completeAuthentication = useCallback(() => {
    if (variant === "login") {
      // `useStytchUser` owns the authenticated boundary. Wait for its user
      // value before resetting Home so the logged-out Home auth gate can
      // never flash between OTP completion and session propagation.
      return;
    }
    setPhase("saving");
  }, [variant]);

  const handleRetryOnboardingSave = useCallback(() => {
    setError(null);
    setPhase("saving");
  }, []);

  // Invalid path for the RHF submit — the schema failed, so surface the same
  // invalid-phone copy the ad-hoc guard used to show.
  const handlePhoneInvalid = () => {
    setError(t("onboarding.create_account.error_invalid_phone"));
  };

  const handleSendCode = async () => {
    const digits = phoneRaw.replace(/\D/g, "");

    setIsSubmitting(true);
    setError(null);

    try {
      const formatted = toE164PhoneNumber(country.dialCode, digits);
      const resp = await stytch.otps.sms.loginOrCreate(formatted, {
        expiration_minutes: 10,
      });
      setMethodId(resp.method_id);
      setStep("otp");
      setOtpResetKey((current) => current + 1);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      console.error("SMS OTP send error:", err);
      const isPhoneError =
        stytchErrorType(err)?.startsWith("invalid_phone_number") ?? false;
      setError(
        t(
          isPhoneError
            ? "onboarding.create_account.error_invalid_phone"
            : "onboarding.create_account.error_send_failed",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (code: string) => {
    // Explicit, user-facing OTP validation (#1545) — the code field used to
    // fail silently on a short/non-numeric value. The 6-digit OTPInput calls
    // this on completion, but a paste/programmatic path could deliver a bad
    // value; validate against the shared schema and surface a clear error.
    if (!createAccountOtpSchema.safeParse({ code }).success) {
      setError(t("onboarding.create_account.error_invalid_code"));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await stytch.otps.authenticate(code, methodId, {
        session_duration_minutes: 60 * 24,
      });
      completeAuthentication();
    } catch (err) {
      console.error("OTP verify error:", err);
      // otp_code_not_found / unable_to_auth_otp_code mean a wrong or expired
      // code; anything else (network, session, rate limit) gets the generic
      // copy rather than the raw Stytch string.
      const errorType = stytchErrorType(err);
      const isCodeError =
        errorType === "otp_code_not_found" ||
        errorType === "unable_to_auth_otp_code";
      setError(
        t(
          isCodeError
            ? "onboarding.create_account.error_invalid_code"
            : "onboarding.create_account.error_verify_failed",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (isSubmitting || resendCooldown > 0) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const digits = phoneRaw.replace(/\D/g, "");
      const formatted = toE164PhoneNumber(country.dialCode, digits);
      const resp = await stytch.otps.sms.loginOrCreate(formatted, {
        expiration_minutes: 10,
      });
      setMethodId(resp.method_id);
      setOtpResetKey((current) => current + 1);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      console.error("Resend OTP error:", err);
      setError(t("onboarding.create_account.error_resend_failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSocialAuth = async (provider: "google" | "apple") => {
    setError(null);
    await startOAuth(provider);
  };

  const handlePasskeySignIn = async () => {
    setError(null);
    try {
      await authenticatePasskey.mutateAsync();
    } catch (err) {
      // Closing or timing out the browser credential sheet is a deliberate
      // cancellation, so leave the login surface untouched without an error.
      if (err instanceof Error && err.name === "NotAllowedError") return;
      setError(t("onboarding.login.passkey_error"));
    }
  };

  const handleGetStarted = () => {
    navigation.navigate("WelcomeGoals");
  };

  const testSignIn = useTestSignIn({ onSuccess: completeAuthentication });

  const isDisabled =
    isSubmitting || isOAuthLoading || authenticatePasskey.isPending;
  const isLoginOtpStep =
    variant === "login" && phase === "welcome" && step === "otp";
  const partnerRecoveryBody =
    partnerInviteFailure === "expired"
      ? t("onboarding.create_account.partner_invite_expired")
      : partnerInviteFailure === "already_used"
        ? t("onboarding.create_account.partner_invite_already_used")
        : partnerInviteFailure === "cancelled"
          ? t("onboarding.create_account.partner_invite_cancelled")
          : partnerInviteFailure === "invalid"
            ? t("onboarding.create_account.partner_invite_invalid")
            : t("onboarding.create_account.partner_invite_unavailable");

  return (
    <OnboardingShell>
      <View
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent:
            phase === "loading" ||
            phase === "saving" ||
            phase === "setup-recovery" ||
            phase === "partner-recovery"
              ? "center"
              : "flex-start",
          paddingTop: phase === "welcome" ? (isLoginOtpStep ? 16 : 32) : 0,
        }}
      >
        {(variant !== "login" || step === "otp") && (
          <Animated.View
            style={{
              opacity: phase === "saving" ? 1 : orbOpacity,
              transform: [{ scale: phase === "saving" ? 1 : orbScale }],
              marginBottom: phase === "welcome" ? 12 : 40,
            }}
          >
            <LouiseMark
              accessibilityLabel="Louise"
              size={phase === "welcome" ? 44 : 72}
            />
          </Animated.View>
        )}

        {/* Saving phase — shown after auth while API calls fire */}
        {phase === "saving" && (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              color: semanticColors.onDark.text.secondary,
              textAlign: "center",
            }}
          >
            {t("onboarding.create_account.saving")}
          </Text>
        )}

        {phase === "setup-recovery" && (
          <View
            testID="onboarding-save-recovery"
            style={{
              width: "100%",
              maxWidth: 420,
              paddingHorizontal: 28,
              alignItems: "center",
            }}
          >
            <MovingGradientTitle
              style={{
                fontFamily: "Tobias_300Light",
                fontSize: 36,
                color: semanticColors.onDark.text.primary,
                lineHeight: 42,
                letterSpacing: -0.8,
                textAlign: "center",
              }}
            >
              {t("onboarding.create_account.setup_recovery_title")}
            </MovingGradientTitle>
            <Text
              accessibilityRole="alert"
              className="font-dm-sans"
              style={{
                fontSize: 14,
                lineHeight: 21,
                color: semanticColors.onDark.text.secondary,
                textAlign: "center",
                marginTop: 12,
                marginBottom: 24,
              }}
            >
              {t("onboarding.create_account.setup_recovery_body")}
            </Text>
            <OnboardingPrimaryButton
              title={t("onboarding.create_account.retry_setup")}
              onPress={handleRetryOnboardingSave}
              showArrow
            />
          </View>
        )}

        {phase === "partner-recovery" && (
          <View
            testID="partner-invite-recovery"
            style={{
              width: "100%",
              maxWidth: 420,
              paddingHorizontal: 28,
              alignItems: "center",
            }}
          >
            <MovingGradientTitle
              style={{
                fontFamily: "Tobias_300Light",
                fontSize: 36,
                color: semanticColors.onDark.text.primary,
                lineHeight: 42,
                letterSpacing: -0.8,
                textAlign: "center",
              }}
            >
              {t("onboarding.create_account.partner_invite_account_ready")}
            </MovingGradientTitle>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                lineHeight: 21,
                color: semanticColors.onDark.text.secondary,
                textAlign: "center",
                marginTop: 12,
                marginBottom: 24,
              }}
            >
              {partnerRecoveryBody}
            </Text>

            <OnboardingPrimaryButton
              title={
                isRetryingPartnerInvite
                  ? t("onboarding.create_account.partner_invite_retrying")
                  : t("onboarding.create_account.partner_invite_retry")
              }
              onPress={handleRetryPartnerInvite}
              disabled={isRetryingPartnerInvite}
              showArrow
            />

            {partnerInviteFailureStage === "accept" && (
              <Pressable
                testID="partner-invite-continue-unconnected"
                accessibilityRole="button"
                onPress={() => {
                  capturePress(
                    pressLabel(
                      t(
                        "onboarding.create_account.partner_invite_continue_unconnected",
                      ),
                    ),
                    "onboarding.create_account.partner_invite_continue_unconnected",
                  );
                  goHomeUnconnected();
                }}
                disabled={isRetryingPartnerInvite}
                style={({ pressed }) => ({
                  marginTop: 18,
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                  opacity: isRetryingPartnerInvite ? 0.4 : pressed ? 0.62 : 1,
                })}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    color: semanticColors.onDark.text.secondary,
                    textAlign: "center",
                    textDecorationLine: "underline",
                  }}
                >
                  {t(
                    "onboarding.create_account.partner_invite_continue_unconnected",
                  )}
                </Text>
              </Pressable>
            )}
          </View>
        )}

        {/* Phase 1: Loading messages */}
        {phase === "loading" && (
          <Animated.View
            style={{
              opacity: loadingOpacity,
              gap: 14,
              alignItems: "center",
            }}
          >
            {MESSAGE_KEYS.slice(0, visibleCount).map((msg, i) => {
              const opacity = msgOpacities[i];
              if (!opacity) return null;
              return (
                <Animated.Text
                  key={msg.key}
                  className="font-dm-sans"
                  style={{
                    opacity,
                    fontSize: 15,
                    color: semanticColors.onDark.text.secondary,
                    textAlign: "center",
                  }}
                >
                  {t(msg.textKey)}
                </Animated.Text>
              );
            })}
          </Animated.View>
        )}

        {/* Phase 2: Welcome + Auth */}
        {phase === "welcome" && (
          <View
            style={{
              width: "100%",
              paddingHorizontal: 32,
              alignItems: "center",
            }}
          >
            {/* Welcome header */}
            <Animated.View
              style={{
                opacity: welcomeOpacity,
                alignItems: "center",
                marginBottom: 28,
              }}
            >
              <MovingGradientTitle
                style={{
                  fontFamily: "Tobias_300Light",
                  fontSize: 38,
                  color: semanticColors.onDark.text.primary,
                  lineHeight: 42,
                  letterSpacing: -0.8,
                  textAlign: "center",
                }}
              >
                {step === "otp"
                  ? t("onboarding.create_account.welcome_otp")
                  : variant === "login"
                    ? t("onboarding.login.title")
                    : data.firstName
                      ? t("onboarding.create_account.welcome_with_name", {
                          params: { name: data.firstName },
                        })
                      : t("onboarding.create_account.welcome")}
              </MovingGradientTitle>
              <Text
                className="font-dm-sans"
                style={{
                  maxWidth: 380,
                  alignSelf: "center",
                  fontSize: 14,
                  color: semanticColors.onDark.text.secondary,
                  textAlign: "center",
                  marginTop: 6,
                }}
              >
                {step === "otp"
                  ? t("onboarding.create_account.otp_subtitle", {
                      params: {
                        phone: `${country.dialCode} ${formatMoonPhoneNumber(
                          phoneRaw,
                          country,
                        )}`,
                      },
                    })
                  : t(
                      variant === "login"
                        ? "onboarding.login.phone_subtitle"
                        : "onboarding.create_account.phone_subtitle",
                    )}
              </Text>
            </Animated.View>

            {/* Auth inputs directly on the card */}
            <Animated.View
              style={{
                opacity: authOpacity,
                transform: [{ translateY: authTranslateY }],
                width: "100%",
                maxWidth: 380,
              }}
            >
              {step === "phone" ? (
                <>
                  <MoonPhoneField
                    testID="create-account-phone-shell"
                    value={phoneValue}
                    onChange={(next) => {
                      handlePhoneFieldChange(next);
                      setError(null);
                    }}
                    onCountryChange={handlePhoneCountryChange}
                    onNationalChange={handlePhoneNationalChange}
                    defaultCountry={country.iso}
                    placeholder={t(
                      "onboarding.create_account.phone_placeholder",
                    )}
                    error=""
                    icon={null}
                    appearance="glass"
                    countrySelector="visible"
                    countrySelectorTestID="create-account-country-picker"
                    countrySearchTestID="create-account-country-search"
                    countrySearchPlaceholder={t(
                      "onboarding.create_account.country_search_placeholder",
                    )}
                    countryNoMatchesMessage={(query) =>
                      t("onboarding.create_account.country_search_no_matches", {
                        params: { query },
                      })
                    }
                  />

                  {/* Error message */}
                  {error && (
                    <FieldMessage onDark centered style={{ marginTop: 8 }}>
                      {error}
                    </FieldMessage>
                  )}

                  {error && user && variant === "signup" && (
                    <OnboardingPrimaryButton
                      title={t("onboarding.create_account.retry_setup")}
                      onPress={handleRetryOnboardingSave}
                      style={{ marginTop: 16 }}
                    />
                  )}

                  {/* Continue button */}
                  <View style={{ marginTop: 16 }}>
                    <OnboardingPrimaryButton
                      title={
                        isSubmitting
                          ? t("onboarding.create_account.sending")
                          : t("onboarding.continue")
                      }
                      onPress={phoneForm.handleSubmit(
                        handleSendCode,
                        handlePhoneInvalid,
                      )}
                      disabled={isDisabled}
                      showArrow
                    />
                  </View>

                  <AuthDivider
                    label={t(
                      variant === "login"
                        ? "onboarding.login.or_sign_in_with"
                        : "onboarding.create_account.or_sign_up_with",
                    )}
                  />

                  {passkeyAvailable && (
                    <GlassButton
                      variant="frosted"
                      size="large"
                      testID="splash-login-passkey"
                      accessibilityLabel={t("onboarding.login.passkey")}
                      disabled={isDisabled}
                      onPress={handlePasskeySignIn}
                      loading={authenticatePasskey.isPending}
                      label={t("onboarding.login.passkey")}
                      icon={
                        <KeyRound
                          size={17}
                          color={semanticColors.onDark.text.primary}
                          strokeWidth={2}
                        />
                      }
                      style={{ marginBottom: 12 }}
                    />
                  )}

                  {/* Social buttons */}
                  <View
                    style={{ width: "100%", flexDirection: "row", gap: 12 }}
                  >
                    <GlassAuthButton
                      testID={`splash-${variant}-google`}
                      label="Google"
                      icon={<GoogleIcon />}
                      onPress={() => handleSocialAuth("google")}
                      disabled={isDisabled}
                      accessibilityLabel={t(
                        variant === "login"
                          ? "onboarding.login.sign_in_with"
                          : "onboarding.create_account.sign_up_with",
                        { params: { provider: "Google" } },
                      )}
                    />
                    <GlassAuthButton
                      testID={`splash-${variant}-apple`}
                      label="Apple"
                      icon={<AppleIcon />}
                      onPress={() => handleSocialAuth("apple")}
                      disabled={isDisabled}
                      accessibilityLabel={t(
                        variant === "login"
                          ? "onboarding.login.sign_in_with"
                          : "onboarding.create_account.sign_up_with",
                        { params: { provider: "Apple" } },
                      )}
                    />
                  </View>

                  {variant === "login" && (
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: 24,
                      }}
                    >
                      {/* The question stays dim; the action reads as the
                          tappable part, with an arrow onward. */}
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12.5,
                          lineHeight: 16,
                          color: semanticColors.onDark.text.tertiary,
                        }}
                      >
                        {t("onboarding.login.new_to_louise")}
                      </Text>
                      <TextActionButton
                        testID="splash-login-get-started"
                        label={t("onboarding.login.get_started")}
                        accessibilityLabel={t(
                          "onboarding.login.new_to_louise_get_started",
                        )}
                        action="onboarding.login.get_started"
                        onPress={handleGetStarted}
                        tone="onDark"
                        size="sm"
                        icon={ArrowRight}
                        iconPosition="trailing"
                        style={{ paddingHorizontal: 6 }}
                      />
                    </View>
                  )}

                  {/* Hidden test-only email/password sign-in */}
                  {testSignIn.visible && (
                    <View style={{ marginTop: 20, gap: 10 }}>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 11,
                          color: EMBER.colors.textFaint,
                          textAlign: "center",
                          textTransform: "uppercase",
                          letterSpacing: 1.2,
                        }}
                      >
                        {t("onboarding.create_account.test_sign_in")}
                      </Text>
                      <TextInput
                        testID="test-signin-email"
                        placeholder={t("onboarding.create_account.test_email")}
                        placeholderTextColor={EMBER.colors.textFaint}
                        autoCapitalize="none"
                        autoComplete="email"
                        keyboardType="email-address"
                        value={testSignIn.email}
                        onChangeText={testSignIn.setEmail}
                        style={{
                          borderWidth: 1,
                          borderColor: "rgba(255, 255, 255, 0.9)",
                          backgroundColor: "rgba(255, 255, 255, 0.82)",
                          borderRadius: 999,
                          paddingHorizontal: 16,
                          paddingVertical: 12,
                          fontSize: 14,
                          color: EMBER.colors.text,
                          fontFamily: "DMSans_400Regular",
                        }}
                      />
                      <TextInput
                        testID="test-signin-password"
                        placeholder={t(
                          "onboarding.create_account.test_password",
                        )}
                        placeholderTextColor={EMBER.colors.textFaint}
                        secureTextEntry
                        autoCapitalize="none"
                        autoComplete="password"
                        value={testSignIn.password}
                        onChangeText={testSignIn.setPassword}
                        style={{
                          borderWidth: 1,
                          borderColor: "rgba(255, 255, 255, 0.9)",
                          backgroundColor: "rgba(255, 255, 255, 0.82)",
                          borderRadius: 999,
                          paddingHorizontal: 16,
                          paddingVertical: 12,
                          fontSize: 14,
                          color: EMBER.colors.text,
                          fontFamily: "DMSans_400Regular",
                        }}
                      />
                      <Pressable
                        testID="test-signin-submit"
                        onPress={testSignIn.submit}
                        disabled={testSignIn.isSubmitting}
                        style={{
                          borderWidth: 1,
                          borderColor: "rgba(255, 255, 255, 0.9)",
                          backgroundColor: "rgba(255, 255, 255, 0.82)",
                          borderRadius: 999,
                          paddingVertical: 12,
                          alignItems: "center",
                          opacity: testSignIn.isSubmitting ? 0.6 : 1,
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            fontWeight: "500",
                            color: EMBER.colors.text,
                          }}
                        >
                          {testSignIn.isSubmitting
                            ? t("onboarding.create_account.test_signin_loading")
                            : t("onboarding.create_account.test_signin_button")}
                        </Text>
                      </Pressable>
                      {testSignIn.error && (
                        <FieldMessage
                          testID="test-signin-error"
                          onDark
                          centered
                        >
                          {testSignIn.error}
                        </FieldMessage>
                      )}
                    </View>
                  )}
                </>
              ) : (
                <>
                  {/* OTP input */}
                  <View style={{ alignItems: "center" }}>
                    <OTPInput
                      key={otpResetKey}
                      length={6}
                      onComplete={handleVerifyCode}
                      onChange={() => setError(null)}
                      onInvalidInput={() =>
                        setError(
                          t("onboarding.create_account.error_invalid_code"),
                        )
                      }
                      appearance="glass"
                    />
                  </View>

                  {/* Error message */}
                  {error && (
                    <FieldMessage onDark centered style={{ marginTop: 12 }}>
                      {error}
                    </FieldMessage>
                  )}

                  {isSubmitting && (
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        color: semanticColors.onDark.text.secondary,
                        textAlign: "center",
                        marginTop: 12,
                      }}
                    >
                      {t("onboarding.create_account.verifying")}
                    </Text>
                  )}

                  {/* Resend / Use different number */}
                  <View
                    style={{
                      width: "100%",
                      maxWidth: 240,
                      alignSelf: "center",
                      gap: 10,
                      marginTop: 24,
                    }}
                  >
                    <TextActionButton
                      testID="otp-resend-code"
                      label={
                        resendCooldown > 0
                          ? t(
                              "onboarding.create_account.resend_code_countdown",
                              { params: { seconds: String(resendCooldown) } },
                            )
                          : t("onboarding.create_account.resend_code")
                      }
                      action="onboarding.create_account.resend_code"
                      onPress={handleResendCode}
                      disabled={isSubmitting || resendCooldown > 0}
                      tone="onDark"
                      size="sm"
                      icon={RotateCcw}
                      centered
                    />

                    <TextActionButton
                      testID="otp-use-different-number"
                      label={t(
                        "onboarding.create_account.use_different_number",
                      )}
                      action="onboarding.create_account.use_different_number"
                      onPress={() => {
                        setStep("phone");
                        setError(null);
                        setResendCooldown(0);
                      }}
                      tone="onDark"
                      size="sm"
                      icon={Phone}
                      centered
                    />
                  </View>
                </>
              )}
            </Animated.View>
          </View>
        )}
      </View>
    </OnboardingShell>
  );
}
